/**
 * The employee's own attendance record.
 *
 * Shows the arithmetic rather than just a verdict — "12 of 16 check-ins" next
 * to "75%" — because an availability number that decides pay must be
 * challengeable, and you cannot challenge a figure you cannot see derived.
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2, ShieldAlert } from 'lucide-react';
import { client } from '../api/client';
import ShiftBar from '../components/ShiftBar';
import type { AttendanceDay, Page, Ping } from '../types';

const STATUS_TONE: Record<AttendanceDay['status'], string> = {
  present: 'bg-emerald-100 text-emerald-700',
  half_day: 'bg-amber-100 text-amber-700',
  absent: 'bg-red-100 text-red-700',
  leave: 'bg-blue-100 text-blue-700',
  holiday: 'bg-gray-100 text-gray-600',
  weekly_off: 'bg-gray-100 text-gray-600',
  // Slate, not red and not green: nothing was measured, so the badge
  // must not read as either a good day or a bad one.
  unmonitored: 'bg-slate-100 text-slate-600',
};

/**
 * Minutes as hours and minutes.
 *
 * Zero renders as a dash rather than "0h 0m": a shift still open has no worked
 * time yet, and a hard zero reads as a claim that nothing was done.
 */
function formatWorked(minutes: number): string {
  if (!minutes) return '—';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

export default function AttendancePage() {
  const { data: attendance, isLoading } = useQuery<Page<AttendanceDay>>({
    queryKey: ['my-attendance'],
    queryFn: async () =>
      (await client.get('/api/v1/attendance/me', { params: { limit: 31 } })).data,
  });

  const { data: todaysPings } = useQuery<Ping[]>({
    queryKey: ['todays-pings'],
    queryFn: async () => (await client.get('/api/v1/attendance/ping/today')).data,
  });

  const rows = attendance?.items ?? [];
  /**
   * Hours on the clock over the window shown.
   *
   * Counted over days that produced any, so the average is hours per day
   * worked rather than per calendar day — a weekend in the window would
   * otherwise drag it down and describe nothing.
   */
  const totalWorked = rows.reduce((sum, day) => sum + day.workedMinutes, 0);
  const daysWorked = rows.filter((day) => day.workedMinutes > 0).length;

  return (
    <div className="space-y-6">
      <ShiftBar />

      {todaysPings && todaysPings.length > 0 && (
        <section className="card">
          <h2 className="mb-3 text-sm font-semibold text-gray-900">
            Today's Check-ins
          </h2>
          <div className="flex flex-wrap gap-1.5">
            {todaysPings.map((ping) => (
              <span
                key={ping.id}
                title={`${new Date(ping.dueAt).toLocaleTimeString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit',
                })} — ${ping.status}`}
                className={`h-7 rounded px-2 text-xs leading-7 ${
                  ping.status === 'responded'
                    ? 'bg-emerald-100 text-emerald-700'
                    : ping.status === 'missed'
                      ? 'bg-red-100 text-red-700'
                      : ping.status === 'skipped'
                        ? 'bg-gray-100 text-gray-400'
                        : 'bg-gray-100 text-gray-500'
                }`}
              >
                {new Date(ping.dueAt).toLocaleTimeString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Times are set by the server and vary within each 10-minute slot.
          </p>
        </section>
      )}

      <section>
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">Last 31 Days</h2>
          {/* Summed from the same worked_minutes each row shows, so the total
              and the rows can never disagree. Overlapping sessions were
              already merged server-side when each day was rolled up, which is
              why this is a plain sum and not a second calculation.

              Days still running contribute nothing: a shift is not counted
              until it is clocked out, so today's hours appear tomorrow rather
              than creeping upward all afternoon. */}
          {rows.length > 0 && (
            <p className="text-xs text-gray-500">
              <span className="font-medium text-gray-900">
                {formatWorked(totalWorked)}
              </span>{' '}
              across {daysWorked} {daysWorked === 1 ? 'day' : 'days'}
              {daysWorked > 0 && (
                <> · {formatWorked(Math.round(totalWorked / daysWorked))} average</>
              )}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Worked</th>
                  <th className="px-4 py-2.5 font-medium">Availability</th>
                  <th className="px-4 py-2.5 font-medium">Check-ins</th>
                  <th className="px-4 py-2.5 font-medium">Window</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {attendance?.items.map((day) => (
                  <tr key={day.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-900">
                      {day.workDate}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`badge ${STATUS_TONE[day.status]}`}>
                        {day.status.replace(/_/g, ' ')}
                      </span>
                      {/* Moved here when the On site column went. Flags are
                          surfaced for review and never auto-punish, so losing
                          them with the geofence figure would have dropped a
                          separate signal without saying so. */}
                      {day.anomalyFlags && day.anomalyFlags.length > 0 && (
                        <span
                          className="ml-1 inline-flex"
                          title={day.anomalyFlags.join(', ')}
                        >
                          <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
                        </span>
                      )}
                      {day.isManualOverride && (
                        <span className="badge ml-1 bg-indigo-100 text-indigo-700">
                          adjusted
                        </span>
                      )}
                    </td>
                    {/* Hours on the clock, which is not the same question as
                        availability — an unmonitored day has no check-ins to
                        score and can still be a full day's work. */}
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-900">
                      {formatWorked(day.workedMinutes)}
                    </td>
                    <td className="px-4 py-2.5">
                      <AvailabilityBar pct={Number(day.availabilityPct)} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {day.respondedPings}/{day.expectedPings}
                      {day.missedPings > 0 && (
                        <span className="ml-1 text-red-500">
                          ({day.missedPings} missed)
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                      {day.firstActivityAt
                        ? `${time(day.firstActivityAt)} – ${time(day.lastActivityAt)}`
                        : '—'}
                    </td>
                  </tr>
                ))}
                {attendance?.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                      No attendance recorded yet. Start a shift to begin.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function AvailabilityBar({ pct }: { pct: number }) {
  const tone =
    pct >= 75 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="tabular-nums text-gray-700">{pct.toFixed(0)}%</span>
    </div>
  );
}

function time(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
